'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Card, CardContent, CardHeader } from '@eliadmin/core/src/components/ui/card';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { HTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { PiDotOutlineFill } from 'react-icons/pi';
type CategorySliderClassName = {
  main?: string;
  controlButton?: string;
  list?: string;
  listItem?: string;
  listItemImage?: string;
  listItemChevron?: string;
  link?: string;

  hoverCard?: string;
  hoverCardHeader?: string;
  hoverCardHeaderTitle?: string;
  hoverCardContent?: string;
  hoverCardList?: string;
  hoverCardListItem?: string;
  hoverCardLink?: string;
};

export interface CategorySliderProps {
  className?: CategorySliderClassName;
  style?: HTMLAttributes<HTMLElement>['style'];
  categories?: CategoriesEntity[];
  expandable?: boolean;
  showImage?: boolean;
}

export const CategorySlider = ({
  className,
  style,
  categories: propsCategories,
  expandable = true,
  showImage = false,
}: CategorySliderProps) => {
  const containerRef = useRef<HTMLUListElement | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
  const [showButtons, setShowButtons] = useState(false);

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(state => state.generalLayout);

  const scroll = (delta: number) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const currentScrollLeft = container.scrollLeft;
    container.scrollTo({
      left: currentScrollLeft + delta,
      behavior: 'smooth',
    });
  };

  const handleCategoryHover = (index: number | null) => {
    setHoveredCategory(index);
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        if (container.scrollWidth > container.clientWidth) {
          setShowButtons(true);
        } else {
          setShowButtons(false);
        }
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const rawCategories = propsCategories || generalLayout.header?.categories;
  const categories = rawCategories?.filter(
    (cat, i, arr) => arr.findIndex(c => c.name === cat.name) === i,
  );

  return (
    // <div
    //   className={cn([
    //     'flex h-8 w-full flex-row items-center justify-center bg-foreground text-white',
    //     className,
    //   ])}
    // >
    //   <Container size="xl" className={'relative flex items-center justify-center'}>
    <nav
      className={cn(
        'relative flex items-center justify-center py-1',
        'ea-category-slider__main',
        className?.main,
      )}
      style={style}
    >
      {showButtons && (
        <LuChevronLeft
          onClick={() => scroll(-100)}
          className={cn(
            'mr-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
      <ul
        ref={containerRef}
        className={cn(
          'hide-scroll-bar flex flex-row overflow-x-scroll will-change-scroll',
          'ea-category-slider__list',
          className?.list,
        )}
      >
        {categories?.map((category, index) => {
          // we have to do this because menu items can be used here to
          const LinkComp = category.slug
            ? isRelativeHref(getSafeHref(category.slug))
              ? Link
              : 'a'
            : 'span';

          return (
            <li
              key={index}
              className={cn(
                'flex min-w-max flex-row items-center px-3 py-1 text-foreground',
                'ea-category-slider__list-item',
                className?.listItem,
              )}
              onMouseEnter={() => {
                if (!category.childs?.length) return;
                if (!expandable) return;
                handleCategoryHover(index);
              }}
              onMouseLeave={() => {
                if (!category.childs?.length) return;
                if (!expandable) return;
                handleCategoryHover(null);
              }}
            >
              <LinkComp {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
                href={getSafeHref(category.slug)}
                className={cn(
                  'flex flex-row items-center gap-2 text-[14px] font-semibold hover:underline',
                  'ea-category-slider__list-item-link',
                  className?.link,
                )}
                prefetch={false}
              >
                {category.image && showImage && (
                  <Image
                    alt={resolveImageAlt(category?.image, category?.name, undefined)}
                    src={category.image.url}
                    width={0}
                    height={0}
                    sizes={'100vw'}
                    className={cn(
                      'h-5 w-5',
                      'ea-category-slider__list-item-image',
                      className?.listItemImage,
                    )}
                  />
                )}
                {category.name}
              </LinkComp>
              {/* {!!category.childs?.length && expandable && (
                <LuChevronDown
                  className={cn(
                    'mx-2 inline-block stroke-primary',
                    'ea-category-slider__list-item-chevron',
                    className?.listItemChevron,
                  )}
                  size={16}
                />
              )} */}

              {hoveredCategory === index && category.childs && (
                <Card
                  className={cn(
                    'absolute right-0 top-[29px] z-10 w-full animate-fadeIn rounded-xl shadow-xl',
                    'ea-category-slider__hover-card',
                    className?.hoverCard,
                  )}
                >
                  <CardHeader
                    className={cn(
                      'flex flex-row items-center justify-between px-4 py-2',
                      'ea-category-slider__hover-card-header',
                      className?.hoverCardHeader,
                    )}
                  >
                    {/* <h4 className={cn('mb-2 text-lg font-bold', className?.hoverCardHeader)}>
                    {category.name}
                  </h4> */}
                  </CardHeader>
                  <CardContent
                    className={cn(
                      'ea-category-slider__hover-card-content',
                      className?.hoverCardContent,
                    )}
                  >
                    <ul
                      className={cn(
                        // 'grid w-full grid-cols-12 gap-4 columns-4',
                        'columns-4',
                        'ea-category-slider__hover-card-list',
                        className?.hoverCardList,
                      )}
                    >
                      {category.childs.map(childCategory => (
                        <li
                          key={`${childCategory.name}-${childCategory.slug}`}
                          className={cn(
                            'col-span-6 mb-3 flex break-inside-avoid flex-col gap-2 text-sm font-medium md:col-span-4 lg:col-span-3',
                            'min-w-[250px]',
                            'ea-category__hover-card-list-item',
                            className?.hoverCardListItem,
                          )}
                        >
                          <Link prefetchOnHover
                            href={childCategory.slug}
                            className={cn(
                              'px-6 text-sm font-bold text-primary hover:underline',
                              'ea-category-slider__hover-card-link',
                              className?.hoverCardLink,
                            )}
                            prefetch={false}
                          >
                            {childCategory.name}
                          </Link>
                          <ul>
                            {childCategory.childs?.map((grandChildCategory, i) => {
                              if (i > CATEGORY_CONFIG.categoryNavigation.displayGrandchildrenCount)
                                return null;
                              if (
                                i === CATEGORY_CONFIG.categoryNavigation.displayGrandchildrenCount
                              )
                                return (
                                  <Link prefetchOnHover
                                    key={`${childCategory.name}-${childCategory.slug}-more`}
                                    href={childCategory.slug}
                                    className={cn(
                                      'flex w-full items-center justify-center px-6 text-sm font-bold text-primary hover:underline',
                                      'ea-category-slider__hover-card-link',
                                      className?.hoverCardLink,
                                    )}
                                    prefetch={false}
                                  >
                                    {t('text668')}
                                  </Link>
                                );
                              return (
                                <div
                                  key={`${grandChildCategory.name}-${grandChildCategory.slug}`}
                                  className="flex min-h-6 cursor-pointer items-center py-1 hover:bg-input hover:text-primary"
                                >
                                  <li
                                    className={cn(
                                      'flex h-full flex-grow items-center px-6 text-sm font-medium hover:text-primary hover:no-underline',
                                      'ea-category__hover-card-list-item',
                                      className?.hoverCardListItem,
                                    )}
                                  >
                                    <Link prefetchOnHover
                                      href={grandChildCategory.slug}
                                      className={cn(
                                        'flex h-full flex-grow items-center text-sm font-semibold',
                                        'ea-category-slider__hover-card-link',
                                        className?.hoverCardLink,
                                      )}
                                      prefetch={false}
                                    >
                                      <div className="flex w-full flex-grow items-start">
                                        <PiDotOutlineFill
                                          className="mt-[2px] h-5 w-5 flex-1"
                                          style={{
                                            width: '20px !important',
                                            height: '20px !important',
                                          }}
                                        />
                                        <p className="flex-[6]">{grandChildCategory.name}</p>
                                      </div>
                                    </Link>
                                  </li>
                                </div>
                              );
                            })}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </li>
          );
        })}
      </ul>
      {showButtons && (
        <LuChevronRight
          onClick={() => scroll(100)}
          className={cn(
            'ml-auto h-6 w-6 shrink-0 cursor-pointer',
            'ea-category-slider__control-button',
            className?.controlButton,
          )}
        />
      )}
    </nav>
    //   </Container>
    // </div>
  );
};
